import React, { useEffect, memo, useMemo, useState } from "react";
import { createClient } from "@liveblocks/client";
import { ClientSideSuspense, LiveblocksProvider, RoomProvider } from "@liveblocks/react/suspense";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Helmet } from "react-helmet";
import { LiveMap } from "@liveblocks/core";
import { StyledLoader } from "pages/flyer/builder/drawer/styled.ts";
import { Editor } from "pages/flyer/builder";
import { useParams, useHistory } from "react-router-dom";
import { authorization } from "module/auth";
import { apiClient } from "module/api";
import CONFIG from "config/config";

import { TldrawApp } from "./drawer/state";
import { useStore } from "../../../store";
import { useBrandKitStore } from "../../../store/brandKit";
import { getRoomId } from "./utils";
import useTLDrawStorage from "./hooks/useTLDrawStorage";
import { LoadingUI } from "./drawer/components/Loading";
import { buildLiveblocksAuthEndpoint, buildLiveblocksUserPayload } from "./utils/liveblocksAuth";
import { clearGuestRoom } from "./utils/clearGuestRoom";
import BrowserWarningModal from "../../../components/BrowserWarning/BrowserWarningModal";
import MobileBlockerModal from "../../../components/MobileBlocker/MobileBlockerModal";
import { getFlyerBuilderState } from "graphQL/query/flyerBuilderStates";
import { setStoredOrientation } from "./utils/orientationStorage";
import { PublicBuilderFigmaEditGate } from "./FigmaEditGate";

const authEndpointUrl = process.env.REACT_APP_LIVEBLOCKS_AUTH_URL;

/**
 * Module-scope flag that survives React unmount/remount cycles (so it
 * persists across SPA navigations within the same page-load session)
 * but RESETS on a full page reload — when the JS bundle re-evaluates
 * and this variable goes back to its initial `false` value. Used by
 * the guest-room cleanup below to fire exactly once per page-load
 * session, regardless of how many times React Router re-mounts the
 * public builder component along the way (format pick, etc.).
 *
 * Scope of "reload" that triggers a reset is intentionally limited to
 * F5 / Cmd+R / `location.reload()` — refresh-button + Enter-on-URL
 * variants may not re-evaluate the module in every browser (bfcache,
 * HMR), and supporting those cases reliably required listeners that
 * weren't worth the complexity. Users get a clean reset on the two
 * primary refresh keystrokes; anyone Entering-on-URL just sees the
 * cached canvas state, which is acceptable.
 */
let _guestSessionInitialMountHandled = false;

export const getRoomStorage = async (roomId) => {
  try {
    const liveblocks = createClient({
      authEndpoint: buildLiveblocksAuthEndpoint(authEndpointUrl),
    });
    const { room } = liveblocks.enterRoom(roomId);
    const storage = await room?.getStorage();
    const res = storage.root.toObject();
    return res;
  } catch (error) {
    console.log("getRoomStorage_error", error);
    return null;
  }
};

const PublicFlyerBuilderPage = () => {
  const { roomId: roomIdParam, countryCode, format: flyerType } = useParams();
  const history = useHistory();
  const roomId = getRoomId(roomIdParam);
  const designId = roomIdParam;

  const { storage, loading } = useTLDrawStorage();

  const user = authorization.getUser();

  // Load Brand Kit on mount so default font is available for new text/placeholders
  const loadBrandKit = useBrandKitStore((s) => s.loadBrandKit);
  const [brandKitLoaded, setBrandKitLoaded] = useState(false);
  useEffect(() => {
    loadBrandKit().finally(() => setBrandKitLoaded(true));
  }, [loadBrandKit]);

  // Guest rooms get a fresh-slate reset on the FIRST React mount of
  // every page-load session — F5/Cmd+R reload, typing the URL and
  // hitting Enter, clicking a link that lands on this page, or even
  // the initial first-ever visit. All of these re-evaluate the JS
  // bundle, which is what resets the `_guestSessionInitialMountHandled`
  // module flag back to `false`. SPA navigations within the same
  // session (format pick → component unmount+remount because the
  // matched route flips between `PUBLIC_FLYER_BUILDER_NEW` and
  // `PUBLIC_FLYER_BUILDER`) leave the module flag at `true` and skip
  // the wipe entirely, so the user's in-progress canvas survives
  // every in-app navigation.
  //
  // We previously gated this on `PerformanceNavigationTiming.type ===
  // "reload"` to fire only on explicit refreshes, but "Enter on the
  // URL bar" reports as `"navigate"` — same as a first-visit link
  // click — so the user couldn't reset that way. Dropping the check
  // means: any full page load resets, only SPA navigation persists.
  // First-visit cleanups are no-ops (the room is already empty) so
  // there's no downside to always running the path.
  //
  // The reset wipes:
  //   - Liveblocks `records` LiveMap for the room
  //   - the MongoDB recovery snapshot (shapes/bindings/assets only —
  //     documentSettings stays so orientation/format don't reset)
  //   - the `guestPlaceholdersInjected_*` marker so the placeholder
  //     imagery re-injects when the editor mounts
  //   - the `:format` URL segment, since "no save anything" on a
  //     fresh load means the format pick shouldn't survive either
  //
  // Carry-over is safe: post-signup the user is authed, so the
  // isGuest check short-circuits before we touch any of this.
  //
  // Render is blocked behind `guestRoomCleared` (alongside the
  // existing prefetch / brandkit gates) so the user never sees the
  // previous session's shapes flash briefly before being wiped.
  const [guestRoomCleared, setGuestRoomCleared] = useState(false);

  // True when an already-authenticated user opens the public/marketing entry.
  // We show a CTA that opens the platform in a new tab (see render below)
  // instead of running the guest builder (which hangs for authed users).
  const [authedRedirect, setAuthedRedirect] = useState(false);

  // Two responsibilities, one listener pair (guests only — authed
  // users keep bfcache and don't get prompted to confirm their own
  // reloads):
  //
  //   1. Disqualify guest pages from the back/forward cache. Without
  //      this, Chrome / Safari may treat F5/Cmd+R as a bfcache
  //      restore, preserving module state (including the cleanup
  //      latch) and skipping the reset. The mere presence of a
  //      beforeunload/unload listener opts the page out of bfcache
  //      by spec — `beforeunload` alone is supposed to be enough but
  //      Chrome has been observed to ignore an empty handler, so we
  //      add `unload` too for redundancy.
  //
  //   2. Show the browser's "Are you sure you want to leave?" dialog
  //      whenever the user tries to navigate away — typing a new
  //      URL, hitting reload, closing the tab. Mirrors
  //      `useExitPrompt`'s pattern used in the ADM / EDDM flows.
  //      Browsers ignore custom text since ~2017 for phishing-
  //      prevention reasons; setting `returnValue` to any string is
  //      enough to trigger the dialog with the browser's default
  //      generic message.
  useEffect(() => {
    if (authorization.isLogged?.()) return;
    const handleBeforeUnload = (event) => {
      // Re-check at fire time — the user may have logged in via the
      // SignupModal between mount and unload; in that case we want
      // the post-signup redirect to proceed without prompting.
      if (authorization.isLogged?.()) return;
      event.preventDefault();
      // Both forms required for cross-browser support: the legacy
      // `returnValue` for Chrome/Safari, the function return for
      // Firefox.
      event.returnValue = "";
      return "";
    };
    const noop = () => {};
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("unload", noop);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("unload", noop);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      // Latch — once we've handled the first mount of this page-load
      // session, every subsequent re-mount within the same SPA
      // session is a no-op (format pick → route pattern flips between
      // PUBLIC_FLYER_BUILDER_NEW and PUBLIC_FLYER_BUILDER, etc.).
      // Cleared on real bundle re-evaluation (F5 / Cmd+R), which the
      // bfcache opt-out above ensures actually happens.
      if (_guestSessionInitialMountHandled) {
        if (!cancelled) setGuestRoomCleared(true);
        return;
      }

      if (!roomId) {
        if (!cancelled) setGuestRoomCleared(true);
        return;
      }

      const isGuest = !authorization.isLogged?.();
      if (!isGuest) {
        // Authed user opening the PUBLIC/marketing entry (no `variant`): the
        // guest flow isn't meant for authed users and hangs, so break OUT of
        // the iframe and send them to the platform to start fresh.
        // `variant=integrated` is the platform's OWN embed (and post-signup
        // carry-over) — that IS the real authed builder, so render it normally.
        // Guarding on `!isIntegrated` is critical: without it the platform's
        // embed would redirect → re-embed → redirect in an infinite loop.
        const isIntegrated = new URLSearchParams(window.location.search).get("variant") === "integrated";
        if (!isIntegrated) {
          // Don't auto-open a tab here — a programmatic window.open on load is
          // blocked by popup blockers, and window.top navigation would drag the
          // host page away. Instead render a CTA (below) that opens the platform
          // in a NEW tab on click (user gesture → not blocked).
          if (!cancelled) setAuthedRedirect(true);
          return;
        }
        // Regular client builder or post-signup carry-over — no wipe, no strip.
        _guestSessionInitialMountHandled = true;
        if (!cancelled) setGuestRoomCleared(true);
        return;
      }

      // Strip the `:format` segment if present so the URL reflects
      // the "fresh blank design" state. `history.replace` flips the
      // matched route from PUBLIC_FLYER_BUILDER to
      // PUBLIC_FLYER_BUILDER_NEW, unmounting and re-mounting this
      // component. We intentionally leave the module flag at `false`
      // so the re-mounted effect re-enters here, sees `flyerType`
      // undefined, and proceeds to the actual cleanup below.
      if (flyerType) {
        history.replace(`/design/${countryCode}/${roomIdParam}/builder`);
        return;
      }

      // Set the latch BEFORE awaiting the clear so any concurrent
      // re-render that fires the effect can't double-run the wipe.
      _guestSessionInitialMountHandled = true;
      try {
        await clearGuestRoom(roomId, designId, window.location.pathname);
      } catch (err) {
        // Best-effort — never block the user out of the builder on a
        // cleanup failure. They'll see stale state at worst.
        // eslint-disable-next-line no-console
        console.warn("[PublicBuilder] guest room clear failed:", err);
      } finally {
        if (!cancelled) setGuestRoomCleared(true);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [roomId, flyerType, countryCode, roomIdParam, history, designId]);

  // Pre-fetch the saved documentSettings (orientation/format) from MongoDB so the canvas
  // mounts with the correct values from the very first render. Without this, the canvas
  // mounts with `TldrawApp.defaultDocument.orientation = Portrait` and shapes saved at
  // landscape coords appear briefly displaced before useMultiplayerState's setupDocument
  // re-hydrates. This also seeds localStorage so Settings.tsx + useFlyerLayout pick up
  // the right value on their synchronous first pass.
  const [docSettingsPrefetched, setDocSettingsPrefetched] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        if (!roomId) {
          setDocSettingsPrefetched(true);
          return;
        }
        const data = await getFlyerBuilderState(roomId);
        if (cancelled) return;
        if (data?.documentSettings) {
          if (data.documentSettings.orientation) {
            // Pinned flag carry-over: when a guest signs up, Tldraw.tsx's
            // post-signup handler writes the guest's chosen orientation to
            // `flyerOrientation_<newDesignId>` AND sets
            // `flyerOrientationPinned_<newDesignId>`. The new authed room
            // doesn't have an updated documentSettings server-side yet
            // (the orientation lives only in localStorage at this point),
            // so a naive prefetch would clobber both the localStorage
            // value AND `TldrawApp.defaultDocument.orientation` with the
            // server's default Portrait. We override the prefetched
            // value with the pinned localStorage one for THIS mount so
            // the editor's default document AND the stored orientation
            // both reflect the carried Landscape. Pin is one-shot: it's
            // cleared here so subsequent reloads behave normally
            // (Mongo wins).
            const pinKey = `flyerOrientationPinned_${designId}`;
            let pinnedOrientation = null;
            try {
              if (window.localStorage.getItem(pinKey) === "true") {
                pinnedOrientation = window.localStorage.getItem(`flyerOrientation_${designId}`);
                window.localStorage.removeItem(pinKey);
              }
            } catch {
              /* localStorage unavailable — fall through to server value */
            }

            // An explicit `orientation` URL param (e.g. the Quote Manager's
            // address-box flow forcing an ADM/EDDM postcard to landscape) is
            // authoritative for THIS mount: it must win over the server's saved
            // documentSettings.orientation, which would otherwise clobber both
            // the default document AND the localStorage value Settings.tsx just
            // wrote synchronously — producing the "opens landscape, then rotates
            // to portrait" flip.
            let urlOrientation = null;
            try {
              const rawOrientation = new URLSearchParams(window.location.search).get("orientation");
              if (rawOrientation) {
                const lc = rawOrientation.toLowerCase();
                if (lc === "landscape") urlOrientation = "Landscape";
                else if (lc === "portrait") urlOrientation = "Portrait";
              }
            } catch {
              /* ignore — fall through to pinned/server value */
            }

            const effectiveOrientation = urlOrientation || pinnedOrientation || data.documentSettings.orientation;
            TldrawApp.defaultDocument.orientation = effectiveOrientation;

            if (pinnedOrientation && !urlOrientation) {
              // localStorage already has the pinned value — no need to re-write it.
            } else {
              // Persist the effective orientation: the URL param when present
              // (so every reader agrees), otherwise the server value as before.
              setStoredOrientation(effectiveOrientation, designId, undefined);
            }
          }
          if (data.documentSettings.format) {
            TldrawApp.defaultDocument.format = data.documentSettings.format;
          }
        }
      } catch (err) {
        // Best-effort: if the prefetch fails (network, no design yet), fall through.
        // setupDocument will still run and the cross-browser fix in useMultiplayerState
        // will reconcile the orientation post-mount.
        // eslint-disable-next-line no-console
        console.warn("[PublicBuilder] docSettings prefetch failed:", err);
      } finally {
        if (!cancelled) setDocSettingsPrefetched(true);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [roomId, designId]);

  const { initData } = useStore();

  // Determine if this is a new blank design (no format in URL)
  const isNewBlankDesign = !flyerType;

  useEffect(() => {
    const STATE = {
      campaign: { flyerType: flyerType || "" },
      city: null,
      client: {
        id: user?.clientId,
        name: user?.clientName,
      },
      country: {
        id: "0",
        name: "0",
        baPerLocation: 0,
        citiesList: [],
      },
      costsCalculationData: {
        printingCosts: flyerType
          ? {
              [`${flyerType}#250`]: [
                {
                  id: "0",
                  countryId: "0",
                  label: `${flyerType}#250`,
                  value: 0,
                  flyersCount: 500,
                  index: 0,
                },
              ],
            }
          : {},
      },
      user: { ...user, accessDenied: false },
      missions: [],
    };
    initData(STATE);
  }, []);

  const defaultDocument = TldrawApp.defaultDocument;

  const userPayload = useMemo(
    () => buildLiveblocksUserPayload(user),
    [user?.id, user?.firstName, user?.lastName, user?.email, user?.clientName, user?.profilePicture]
  );
  const authEndpointCallback = useMemo(() => buildLiveblocksAuthEndpoint(authEndpointUrl, userPayload), [userPayload]);
  // Resolve each collaborator's identity individually instead of mapping
  // every userId to the current viewer (which would make every comment/pin
  // render under the viewer's name). Authed viewers get resolved via the
  // same `/platform/users/resolve` batch endpoint as the private builder;
  // unknown / anonymous IDs fall through to undefined so Liveblocks can
  // apply its own default.
  const resolveUsers = useMemo(() => {
    const currentUserId = user?.id ? `user-${user.id}` : null;
    const currentUserName = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.email || "Guest";
    const currentUserAvatar = user?.profilePicture || "";

    return async ({ userIds }) => {
      const toLookup = userIds.filter((id) => id && id !== currentUserId && id.startsWith("user-"));
      const looked = {};
      if (toLookup.length > 0) {
        try {
          const rows = await apiClient.resolveUsersBatch({ ids: toLookup });
          rows.forEach((u) => {
            if (u?.id) looked[u.id] = u;
          });
        } catch {
          /* best-effort — fall through to undefined for unresolved IDs */
        }
      }
      return userIds.map((id) => {
        if (id === currentUserId) {
          return { name: currentUserName, color: "#4FC3F7", avatar: currentUserAvatar };
        }
        const match = looked[id];
        if (match) {
          return {
            name: match.name || "User",
            color: "#4FC3F7",
            avatar: match.avatar || "",
          };
        }
        return undefined;
      });
    };
  }, [user?.id, user?.firstName, user?.lastName, user?.email, user?.profilePicture]);

  // Only block rendering for missing roomId/countryCode/loading — NOT flyerType
  // Also wait for Brand Kit to load so default font is available, for the
  // documentSettings prefetch so the canvas mounts at the correct orientation/format,
  // AND for the guest-room cleanup so we don't paint the previous session's shapes
  // before wiping them.
  // Authed user on the public entry: offer to continue in the platform (new
  // tab). Opening on click keeps it out of the popup blocker and leaves the
  // host page intact.
  if (authedRedirect) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          gap: 14,
          textAlign: "center",
          padding: 24,
          fontFamily: '"Proxima Nova", sans-serif',
        }}
      >
        <p style={{ fontSize: 16, fontWeight: 600, color: "#0C172C", margin: 0 }}>You're already signed in</p>
        <p style={{ fontSize: 13, color: "#798498", margin: 0 }}>Open the Creative Studio to start a new design.</p>
        <button
          onClick={() => window.open(`${CONFIG.PLATFORM_BASE_URL}/dashboard/design-management`, "_blank", "noopener")}
          style={{
            padding: "12px 22px",
            background: "#F43F5E",
            color: "#fff",
            border: 0,
            borderRadius: 8,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: '"Proxima Nova", sans-serif',
          }}
        >
          Open Creative Studio →
        </button>
      </div>
    );
  }

  if (!roomId || !countryCode || loading || !brandKitLoaded || !docSettingsPrefetched || !guestRoomCleared) {
    return (
      <>
        {/* Mount the blocker on the loading-gate path too so mobile
            users see the blocker immediately instead of the spinner. */}
        <MobileBlockerModal />
        <StyledLoader>
          <CircularProgress color="secondary" />
        </StyledLoader>
      </>
    );
  }

  // Set format on document only if we have one
  if (flyerType) {
    defaultDocument.format = flyerType;
  }

  return (
    <>
      <Helmet>
        <title>Flyer Builder</title>
      </Helmet>
      {/* Same hard mobile blocker as the authenticated builder. See
          the matching mount in `pages/flyer/builder/index.js`. */}
      <MobileBlockerModal />
      {/* Same browser-compat nag as the authenticated builder — see
          the matching mount in `pages/flyer/builder/index.js`. */}
      <BrowserWarningModal />
      <LiveblocksProvider authEndpoint={authEndpointCallback} resolveUsers={resolveUsers}>
        <RoomProvider id={roomId} initialPresence={{ presence: undefined }} initialStorage={storage}>
          <ClientSideSuspense fallback={<LoadingUI />}>
            <Editor roomId={roomId} designId={designId} countryCode={countryCode} flyerType={flyerType || ""} />
          </ClientSideSuspense>
        </RoomProvider>
      </LiveblocksProvider>
    </>
  );
};

// A Figma design asks for copy or flatten before the room and canvas mount (PAD-32396).
const GatedPublicFlyerBuilderPage = () => (
  <PublicBuilderFigmaEditGate>
    <PublicFlyerBuilderPage />
  </PublicBuilderFigmaEditGate>
);

export default GatedPublicFlyerBuilderPage;
